import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '@/pages/admin/components/AdminLayout';
import type { AdminMaterial } from '@/mocks/adminData';
import { getMaterial, updateMaterial, uploadMaterialFile } from '@/api/materialInfo';
import { getTree } from '@/api/materialCategory';
import type { CategoryNode, SubCategoryNode, ModelNode } from '@/api/materialCategory';

// ─── helpers ────────────────────────────────────────────────────────────────

/** 根据资料类型获取允许上传的文件扩展名 */
function getAcceptByType(type: AdminMaterial['type']): string {
  switch (type) {
    case 'Video': return '.mp4';
    case 'PDF':   return '.pdf';
    case 'Word':  return '.doc,.docx';
    case 'Excel': return '.xls,.xlsx';
    case 'PPT':   return '.ppt,.pptx';
    default:      return '.pdf,.ppt,.pptx,.xls,.xlsx,.doc,.docx';
  }
}

/** 根据资料类型获取文件上传提示文字 */
function getFormatHint(type: AdminMaterial['type']): string {
  switch (type) {
    case 'Video': return '仅支持 MP4 格式 · 将替换当前文件';
    case 'PDF':   return '仅支持 PDF 格式 · 将替换当前文件';
    case 'Word':  return '支持 DOC、DOCX 格式 · 将替换当前文件';
    case 'Excel': return '支持 XLS、XLSX 格式 · 将替换当前文件';
    case 'PPT':   return '支持 PPT、PPTX 格式 · 将替换当前文件';
    default:      return '将替换当前文件';
  }
}

function detectFileType(fileName: string): AdminMaterial['type'] {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  const videoExts = ['mp4', 'mov', 'webm', 'avi', 'mkv', 'wmv', 'flv'];
  if (videoExts.includes(ext)) return 'Video';
  if (ext === 'pdf') return 'PDF';
  if (ext === 'ppt' || ext === 'pptx') return 'PPT';
  if (ext === 'xls' || ext === 'xlsx') return 'Excel';
  if (ext === 'doc' || ext === 'docx') return 'Word';
  return 'PDF';
}

function findCategoryByModel(tree: CategoryNode[], modelId: string): { primary: CategoryNode; sub: SubCategoryNode; model: ModelNode } | null {
  for (const primary of tree) {
    for (const sub of primary.subCategories) {
      const model = sub.models.find((m) => m.id === modelId);
      if (model) return { primary, sub, model };
    }
  }
  return null;
}

function findPrimaryBySubId(tree: CategoryNode[], subId: string): CategoryNode | undefined {
  return tree.find((c) => c.subCategories.some((s) => s.id === subId));
}

function extractCategoryValue(tree: CategoryNode[], mat: AdminMaterial): { primary: string; sub: string; model: string } {
  if (mat.modelCategory) {
    const found = findCategoryByModel(tree, mat.modelCategory);
    if (found) {
      return { primary: found.primary.id, sub: found.sub.id, model: found.model.id };
    }
  }
  const primary = findPrimaryBySubId(tree, mat.category);
  if (primary) {
    return { primary: primary.id, sub: mat.category, model: '' };
  }
  if (tree.length > 0 && tree[0].subCategories.length > 0) {
    return { primary: tree[0].id, sub: tree[0].subCategories[0].id, model: '' };
  }
  return { primary: '', sub: '', model: '' };
}

// ─── CategoryTreeSelector ────────────────────────────────────────────────────

interface CategoryTreeSelectorProps {
  tree: CategoryNode[];
  value: { primary: string; sub: string; model: string };
  onChange: (v: { primary: string; sub: string; model: string }) => void;
}

function CategoryTreeSelector({ tree, value, onChange }: CategoryTreeSelectorProps) {
  const selectedPrimary = tree.find((c) => c.id === value.primary) ?? tree[0];
  const selectedSub = selectedPrimary?.subCategories?.find((s) => s.id === value.sub) ?? selectedPrimary?.subCategories?.[0];
  const selectedModel = selectedSub?.models?.find((m) => m.id === value.model);

  if (!tree.length || !selectedPrimary) {
    return <p className="text-xs text-gray-400">暂无分类数据</p>;
  }

  const handlePrimaryChange = (primaryId: string) => {
    const primary = tree.find((c) => c.id === primaryId)!;
    const sub = primary.subCategories[0];
    const model = sub?.models?.[0]?.id ?? '';
    onChange({ primary: primaryId, sub: sub?.id ?? '', model });
  };

  const handleSubChange = (subId: string) => {
    const sub = selectedPrimary.subCategories.find((s) => s.id === subId)!;
    const model = sub?.models?.[0]?.id ?? '';
    onChange({ primary: value.primary, sub: subId, model });
  };

  const handleModelChange = (modelId: string) => {
    onChange({ primary: value.primary, sub: value.sub, model: modelId });
  };

  const selectCls =
    'w-full appearance-none pl-3 pr-8 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-gray-400 bg-white cursor-pointer';

  return (
    <div className="space-y-2.5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
        {/* 一级 */}
        <div className="relative">
          <label className="text-xs text-gray-400 mb-1 block">一级分类</label>
          <div className="relative">
            <select value={value.primary} onChange={(e) => handlePrimaryChange(e.target.value)} className={selectCls}>
              {tree.map((c) => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
            <div className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-arrow-down-s-line text-base"></i>
            </div>
          </div>
        </div>
        {/* 二级 */}
        <div className="relative">
          <label className="text-xs text-gray-400 mb-1 block">二级分类</label>
          <div className="relative">
            <select value={value.sub} onChange={(e) => handleSubChange(e.target.value)} className={selectCls}>
              {selectedPrimary.subCategories.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
            <div className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-arrow-down-s-line text-base"></i>
            </div>
          </div>
        </div>
        {/* 三级 */}
        <div className="relative">
          <label className="text-xs text-gray-400 mb-1 block">三级分类</label>
          <div className="relative">
            <select
              value={value.model}
              onChange={(e) => handleModelChange(e.target.value)}
              disabled={selectedSub.models.length === 0}
              className={`${selectCls} ${selectedSub.models.length === 0 ? 'opacity-40 cursor-not-allowed' : ''}`}
            >
              {selectedSub.models.length === 0 ? (
                <option value="">（无三级分类）</option>
              ) : (
                selectedSub.models.map((m) => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))
              )}
            </select>
            <div className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-arrow-down-s-line text-base"></i>
            </div>
          </div>
        </div>
      </div>
      {/* 路径预览 */}
      <div className="flex items-center gap-1.5 px-3 py-2 bg-gray-50 rounded-lg border border-gray-100">
        <i className="ri-map-pin-2-line text-xs text-gray-400"></i>
        <span className="text-xs text-gray-500">
          {selectedPrimary?.label ?? '—'}
          {selectedSub ? ` › ${selectedSub.label}` : ''}
          {selectedModel ? ` › ${selectedModel.label}` : ''}
        </span>
      </div>
    </div>
  );
}

// ─── Section wrapper ─────────────────────────────────────────────────────────

function Section({ title, icon, children }: { title: string; icon: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      <div className="flex items-center gap-2 px-6 py-4 border-b border-gray-100">
        <div className="w-6 h-6 flex items-center justify-center text-gray-500">
          <i className={`${icon} text-base`}></i>
        </div>
        <h2 className="text-sm font-bold text-gray-800">{title}</h2>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

// ─── Field ───────────────────────────────────────────────────────────────────

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs font-semibold text-gray-500 mb-1.5 block uppercase tracking-wide">
        {label}
        {required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

const inputCls = 'w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-gray-400 bg-white transition-colors';

// ─── 主页面 ──────────────────────────────────────────────────────────────────

export default function MaterialEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [draft, setDraft] = useState<AdminMaterial | null>(null);
  const [loading, setLoading] = useState(true);
  const [tree, setTree] = useState<CategoryNode[]>([]);
  const [catValue, setCatValue] = useState<{ primary: string; sub: string; model: string }>({ primary: '', sub: '', model: '' });
  const [fileLabel, setFileLabel] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const editFileInputRef = useRef<HTMLInputElement>(null);
  const editVideoInputRef = useRef<HTMLInputElement>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const coverCanvasRef = useRef<HTMLCanvasElement>(null);

  // 将 coverFile 渲染到 canvas 上，绕过 CSP 对 blob:/data: URL 的限制
  useEffect(() => {
    if (!coverFile) return;
    const canvas = coverCanvasRef.current;
    if (!canvas) return;

    let cancelled = false;
    createImageBitmap(coverFile)
      .then((bitmap) => {
        if (cancelled) { bitmap.close(); return; }
        const ctx = canvas.getContext('2d');
        if (!ctx) { bitmap.close(); return; }
        // 以容器实际尺寸作为画布分辨率
        const rect = canvas.getBoundingClientRect();
        const w = rect.width || 884;
        const h = rect.height || 496;
        canvas.width = w;
        canvas.height = h;
        // object-cover: 等比缩放填满画布，居中裁剪
        const scale = Math.max(w / bitmap.width, h / bitmap.height);
        const dw = bitmap.width * scale;
        const dh = bitmap.height * scale;
        ctx.drawImage(bitmap, (w - dw) / 2, (h - dh) / 2, dw, dh);
        bitmap.close();
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [coverFile]);

  useEffect(() => {
    (async () => {
      try {
        const data = await getTree();
        setTree(data);
      } catch (err) {
        console.error('加载分类树失败', err);
      }
    })();
  }, []);

  useEffect(() => {
    if (!id || tree.length === 0) return;
    (async () => {
      try {
        setLoading(true);
        const item = await getMaterial(id);
        const mat: AdminMaterial = {
          id: item.id,
          title: item.title,
          type: item.type as AdminMaterial['type'],
          pptType: item.pptType as AdminMaterial['pptType'],
          primaryCategory: item.primaryCategory,
          category: item.category,
          modelCategory: item.modelCategory,
          fileSize: item.fileSize,
          uploadDate: typeof item.uploadDate === 'number' ? new Date(item.uploadDate).toISOString().split('T')[0] : item.uploadDate,
          uploadedBy: item.uploadedBy,
          uploadedByName: item.uploadedByName,
          views: item.views,
          downloads: item.downloads,
          previewAllowed: item.previewAllowed,
          downloadAllowed: item.downloadAllowed,
          downloadWatermark: item.downloadWatermark,
          thumbnail: item.thumbnail,
          isVideo: item.isVideo,
          videoDuration: item.videoDuration,
          fileUrl: item.fileUrl,
          description: item.description,
          tags: item.tags,
          confidential: item.confidential,
          confidentialAgents: item.confidentialAgents,
        };
        setDraft(mat);
        setCatValue(extractCategoryValue(tree, mat));
      } catch (err) {
        console.error('加载资料失败', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, tree]);

  if (loading) {
    return (
      <AdminLayout title="加载中..." subtitle="">
        <div className="flex items-center justify-center py-32">
          <i className="ri-loader-4-line animate-spin text-2xl text-gray-400"></i>
          <span className="ml-2 text-sm text-gray-400">加载资料...</span>
        </div>
      </AdminLayout>
    );
  }

  if (!draft) {
    return (
      <AdminLayout title="资源未找到" subtitle="">
        <div className="flex flex-col items-center justify-center py-32 text-gray-400">
          <i className="ri-file-search-line text-5xl mb-4"></i>
          <p className="text-base font-semibold text-gray-600 mb-1">资源不存在</p>
          <p className="text-sm mb-6">您查找的资源不存在或已被删除。</p>
          <button
            onClick={() => navigate('/admin/materials')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white cursor-pointer whitespace-nowrap"
            style={{ background: '#0069BD' }}
          >
            <i className="ri-arrow-left-line text-sm"></i>
            返回资料库
          </button>
        </div>
      </AdminLayout>
    );
  }

  const handleCategoryChange = (v: { primary: string; sub: string; model: string }) => {
    setCatValue(v);
    setDraft((prev) => prev ? ({
      ...prev,
      primaryCategory: v.primary,
      category: v.sub,
      modelCategory: v.model || undefined,
    }) : prev);
  };

  const handleFileChange = (file: File | null) => {
    if (!file) return;
    setFileLabel(file.name);
    setSelectedFile(file);
    setDraft((prev) => prev ? { ...prev, type: detectFileType(file.name) } : prev);
  };

  const handleSave = async () => {
    if (!draft || saving) return;
    try {
      setSaving(true);
      // 如果选择了新文件，先上传
      let newFileUrl = '';
      let newFileSize = '';
      if (selectedFile) {
        const uploadResult = await uploadMaterialFile(selectedFile);
        newFileUrl = uploadResult.url;
        const sizeKB = Math.round(uploadResult.size / 1024);
        newFileSize = sizeKB >= 1024
          ? `${(sizeKB / 1024).toFixed(2)} MB`
          : `${sizeKB} KB`;
      }
      // 如果选择了新封面图，独立上传
      let newCoverUrl = '';
      if (coverFile) {
        try {
          setCoverUploading(true);
          const coverResult = await uploadMaterialFile(coverFile);
          newCoverUrl = coverResult.url;
        } finally {
          setCoverUploading(false);
        }
      }
      // 封面图优先级：新上传的封面 > 新上传的文件（自动缩略图） > 已有封面
      const finalThumbnail = newCoverUrl || (newFileUrl || draft.thumbnail || '');
      await updateMaterial({
        id: draft.id,
        title: draft.title,
        type: draft.type,
        pptType: draft.pptType || '',
        primaryCategory: catValue.primary,
        category: catValue.sub,
        modelCategory: catValue.model,
        description: draft.description || '',
        tags: (draft.tags ?? []).join(','),
        fileUrl: newFileUrl || draft.fileUrl || '',
        videoDuration: draft.videoDuration || '',
        downloadAllowed: draft.downloadAllowed,
        downloadWatermark: draft.downloadWatermark !== false,
        confidential: draft.confidential,
        isVideo: draft.isVideo,
        confidentialAgents: (draft.confidentialAgents ?? []).join(','),
        fileSize: newFileSize || draft.fileSize,
        thumbnail: finalThumbnail,
      });
      setSaved(true);
      setTimeout(() => {
        navigate('/admin/materials');
      }, 800);
    } catch (err) {
      console.error('保存资料失败', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/materials');
  };

  return (
    <AdminLayout
      title="编辑资源"
      subtitle={`正在编辑：${draft.title}`}
    >
      {/* 顶部导航栏 */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={handleCancel}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors cursor-pointer group"
        >
          <i className="ri-arrow-left-line text-base group-hover:-translate-x-0.5 transition-transform"></i>
          返回列表
        </button>
        <div className="flex items-center gap-2.5">
          <button
            onClick={handleCancel}
            className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSave}
            disabled={saved || saving}
            className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white cursor-pointer whitespace-nowrap transition-all disabled:opacity-70"
            style={{ background: '#0069BD' }}
          >
            {saved ? (
              <>
                <i className="ri-check-line text-sm"></i>
                已保存
              </>
            ) : saving ? (
              <>
                <i className="ri-loader-4-line animate-spin text-sm"></i>
                保存中...
              </>
            ) : (
              <>
                <i className="ri-save-line text-sm"></i>
                保存更改
              </>
            )}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* 左侧主内容 */}
        <div className="lg:col-span-2 space-y-5">
          {/* 基本信息 */}
          <Section title="基本信息" icon="ri-information-line">
            <div className="space-y-4">
              <Field label="标题" required>
                <input
                  type="text"
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  className={inputCls}
                  placeholder="请输入资源标题"
                />
              </Field>
              <Field label="描述">
                <textarea
                  value={draft.description || ''}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  rows={4}
                  className={`${inputCls} resize-none`}
                  placeholder="请输入资源描述（可选）"
                />
              </Field>
            </div>
          </Section>

          {/* 分类设置 */}
          <Section title="分类设置" icon="ri-folder-line">
            <CategoryTreeSelector tree={tree} value={catValue} onChange={handleCategoryChange} />
          </Section>

          {/* 文件 / 视频来源 */}
          {draft.isVideo ? (
            <Section title="视频来源" icon="ri-video-line">
              <div className="space-y-4">
                <Field label="视频链接">
                  <input
                    type="url"
                    value={draft.fileUrl || ''}
                    onChange={(e) => setDraft({ ...draft, fileUrl: e.target.value })}
                    placeholder="https://..."
                    className={inputCls}
                  />
                </Field>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-gray-200"></div>
                  <span className="text-xs text-gray-400 font-medium">或上传文件</span>
                  <div className="flex-1 h-px bg-gray-200"></div>
                </div>
                <div
                  onClick={() => editVideoInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-200 hover:border-gray-400 hover:bg-gray-50 rounded-xl p-6 text-center cursor-pointer transition-all"
                >
                  {fileLabel ? (
                    <div className="flex items-center justify-center gap-2">
                      <i className="ri-checkbox-circle-fill text-gray-500 text-base"></i>
                      <span className="text-sm font-semibold text-gray-700 truncate max-w-[320px]">{fileLabel}</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setFileLabel(null); setSelectedFile(null); }}
                        className="w-5 h-5 flex items-center justify-center rounded-full bg-gray-200 text-gray-500 hover:bg-gray-300 cursor-pointer shrink-0"
                      >
                        <i className="ri-close-line text-xs"></i>
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 mx-auto mb-3">
                        <i className="ri-upload-cloud-2-line text-2xl text-gray-400"></i>
                      </div>
                      <p className="text-sm font-semibold text-gray-700">点击替换视频文件</p>
                      <p className="text-xs text-gray-400 mt-1">仅支持 MP4 格式</p>
                    </>
                  )}
                  <input
                    ref={editVideoInputRef}
                    type="file"
                    accept=".mp4"
                    className="hidden"
                    onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
                  />
                </div>
                <Field label="视频时长">
                  <input
                    type="text"
                    value={draft.videoDuration || ''}
                    onChange={(e) => setDraft({ ...draft, videoDuration: e.target.value })}
                    placeholder="例如：18:42"
                    className={inputCls}
                  />
                </Field>
              </div>
            </Section>
          ) : (
            <Section title="替换文件" icon="ri-file-upload-line">
              <div className="space-y-4">
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e) => { e.preventDefault(); setDragging(false); handleFileChange(e.dataTransfer.files?.[0] ?? null); }}
                  onClick={() => editFileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${dragging ? 'border-gray-500 bg-gray-50' : 'border-gray-200 hover:border-gray-400 hover:bg-gray-50'}`}
                >
                  {fileLabel ? (
                    <div className="flex items-center justify-center gap-2">
                      <i className="ri-checkbox-circle-fill text-gray-500 text-xl"></i>
                      <span className="text-sm font-semibold text-gray-700 truncate max-w-[320px]">{fileLabel}</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setFileLabel(null); setSelectedFile(null); }}
                        className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-200 text-gray-500 hover:bg-gray-300 cursor-pointer shrink-0"
                      >
                        <i className="ri-close-line text-sm"></i>
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="w-14 h-14 flex items-center justify-center rounded-full bg-gray-100 mx-auto mb-3">
                        <i className="ri-upload-cloud-2-line text-2xl text-gray-500"></i>
                      </div>
                      <p className="text-sm font-semibold text-gray-700">拖拽文件至此，或点击选择</p>
                      <p className="text-xs text-gray-400 mt-1">{draft ? getFormatHint(draft.type) : '将替换当前文件'}</p>
                    </>
                  )}
                  <input
                    ref={editFileInputRef}
                    type="file"
                    accept={draft ? getAcceptByType(draft.type) : '.pdf,.ppt,.pptx,.xls,.xlsx,.doc,.docx'}
                    className="hidden"
                    onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
                  />
                </div>
                <p className="text-xs text-gray-400 text-center">
                  当前文件：<span className="font-semibold text-gray-600">{draft.title}</span>（{draft.fileSize}）
                </p>
              </div>
            </Section>
          )}

          {/* 封面图 */}
          <Section title="封面图" icon="ri-image-line">
            <div className="space-y-3">
              {/* 当前封面预览 */}
              {(coverPreview || draft.thumbnail) ? (
                <div
                  className="relative rounded-lg overflow-hidden border border-gray-200 bg-gray-100"
                  style={{ aspectRatio: '221/124' }}
                >
                  {coverPreview ? (
                    <canvas ref={coverCanvasRef} className="w-full h-full" />
                  ) : draft.thumbnail ? (
                    <img
                      src={draft.thumbnail}
                      alt="封面图预览"
                      className="w-full h-full object-cover"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  ) : null}
                  {coverPreview && (
                    <button
                      type="button"
                      onClick={() => { setCoverFile(null); setCoverPreview(false); }}
                      className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80 cursor-pointer transition-colors"
                    >
                      <i className="ri-close-line text-sm"></i>
                    </button>
                  )}
                </div>
              ) : (
                <div
                  className="w-full flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 text-gray-400"
                  style={{ aspectRatio: '221/124' }}
                >
                  <i className="ri-image-line text-3xl mb-1"></i>
                  <span className="text-xs">暂无封面图</span>
                </div>
              )}
              {/* 上传按钮 */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => coverInputRef.current?.click()}
                  disabled={coverUploading}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 cursor-pointer transition-colors disabled:opacity-50"
                >
                  {coverUploading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-sm"></i>
                      <span>上传中...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-upload-line text-sm"></i>
                      <span>{coverPreview ? '已选择新封面' : draft.thumbnail ? '更换封面图' : '上传封面图'}</span>
                    </>
                  )}
                </button>
                {draft.thumbnail && !coverPreview && (
                  <button
                    type="button"
                    onClick={() => { setDraft({ ...draft, thumbnail: '' }); }}
                    className="py-2 px-3 rounded-lg border border-red-100 text-sm text-red-500 hover:bg-red-50 cursor-pointer transition-colors"
                    title="移除封面图"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-400">支持 JPG、PNG、GIF 格式，建议尺寸 884×496</p>
              <input
                ref={coverInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.gif"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0] ?? null;
                  if (file) {
                    setCoverFile(file);
                    setCoverPreview(true);
                  }
                  // 重置 input 值，允许重复选择同一文件
                  e.target.value = '';
                }}
              />
            </div>
          </Section>
        </div>

        {/* 右侧设置面板 */}
        <div className="space-y-5">
          {/* 级别 */}
          <Section title="级别设置" icon="ri-shield-line">
            <div className="space-y-2.5">
              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${!draft.confidential ? 'border-gray-400 bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <input
                  type="radio"
                  name="confidential"
                  checked={!draft.confidential}
                  onChange={() => setDraft({ ...draft, confidential: false })}
                  className="cursor-pointer"
                />
                <div>
                  <p className="text-sm font-semibold text-gray-700">普通</p>
                  <p className="text-xs text-gray-400 mt-0.5">所有授权代理商可见</p>
                </div>
              </label>
              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${draft.confidential ? 'border-gray-400 bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <input
                  type="radio"
                  name="confidential"
                  checked={!!draft.confidential}
                  onChange={() => setDraft({ ...draft, confidential: true })}
                  className="cursor-pointer"
                />
                <div>
                  <p className="text-sm font-semibold text-gray-700">机密</p>
                  <p className="text-xs text-gray-400 mt-0.5">仅授权代理商可见</p>
                </div>
              </label>
            </div>
          </Section>

          {/* 下载权限 */}
          <Section title="下载权限" icon="ri-download-line">
              <div className="space-y-2.5">
                <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${draft.downloadAllowed ? 'border-gray-400 bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input
                    type="checkbox"
                    checked={draft.downloadAllowed}
                    onChange={(e) => setDraft({ ...draft, downloadAllowed: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                  />
                  <div>
                    <p className="text-sm font-semibold text-gray-700">允许下载</p>
                    <p className="text-xs text-gray-400 mt-0.5">代理商可下载此文件</p>
                  </div>
                </label>
                {!draft.downloadAllowed && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-100">
                    <i className="ri-eye-line text-xs text-gray-400"></i>
                    <span className="text-xs text-gray-500">当前设置：仅浏览</span>
                  </div>
                )}
              </div>
            </Section>

          {/* 水印设置 */}
          <Section title="水印设置" icon="ri-mark-pen-line">
            <div className="space-y-3">
                {/* 预览水印（始终开启，只读） */}
                <div className="flex items-center justify-between px-3 py-3 rounded-lg bg-amber-50/60 border border-amber-100">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 flex items-center justify-center rounded-md bg-amber-100">
                      <i className="ri-eye-line text-xs text-amber-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">在线预览水印</p>
                      <p className="text-xs text-gray-400 mt-0.5">所有格式在线预览时均叠加动态水印</p>
                    </div>
                  </div>
                  <span className="flex items-center gap-1 text-xs font-semibold text-amber-600 shrink-0">
                    <i className="ri-lock-line text-xs"></i>始终开启
                  </span>
                </div>

                {/* 下载水印：仅 PDF 支持 */}
                {draft.type === 'PDF' ? (
                  <div
                    className={`rounded-lg border p-3 transition-all ${
                      (draft as AdminMaterial & { downloadWatermark?: boolean }).downloadWatermark !== false
                        ? 'border-amber-200 bg-amber-50/40'
                        : 'border-gray-200 bg-gray-50/40'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-7 h-7 rounded-md flex items-center justify-center ${
                          (draft as AdminMaterial & { downloadWatermark?: boolean }).downloadWatermark !== false
                            ? 'bg-amber-100' : 'bg-gray-100'
                        }`}>
                          <i className={`ri-download-line text-xs ${
                            (draft as AdminMaterial & { downloadWatermark?: boolean }).downloadWatermark !== false
                              ? 'text-amber-600' : 'text-gray-400'
                          }`}></i>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-700">下载时叠加水印</p>
                          <p className="text-xs text-gray-400 mt-0.5">PDF 下载文件中嵌入水印</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const current = (draft as AdminMaterial & { downloadWatermark?: boolean }).downloadWatermark;
                          const next = current === false ? true : false;
                          setDraft({ ...draft, downloadWatermark: next } as AdminMaterial & { downloadWatermark?: boolean });
                        }}
                        className="w-11 h-6 rounded-full transition-colors cursor-pointer relative shrink-0"
                        style={{
                          background: (draft as AdminMaterial & { downloadWatermark?: boolean }).downloadWatermark !== false
                            ? '#f59e0b' : '#d1d5db',
                        }}
                      >
                        <span
                          className="absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all duration-200"
                          style={{
                            left: (draft as AdminMaterial & { downloadWatermark?: boolean }).downloadWatermark !== false ? '22px' : '2px',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                          }}
                        ></span>
                      </button>
                    </div>
                    {(draft as AdminMaterial & { downloadWatermark?: boolean }).downloadWatermark === false && (
                      <p className="text-xs text-gray-500 mt-2.5 flex items-center gap-1.5">
                        <i className="ri-information-line text-gray-400"></i>
                        已关闭下载水印，适合本地印刷或对外宣传使用
                      </p>
                    )}
                  </div>
                ) : (
                  // Word / Excel / PPT：无法嵌入下载水印，说明清楚
                  <div className="flex items-start gap-2.5 px-3 py-3 rounded-lg bg-gray-50 border border-gray-200">
                    <i className="ri-information-line text-gray-400 text-sm mt-0.5 shrink-0"></i>
                    <div>
                      <p className="text-xs font-semibold text-gray-600 mb-0.5">下载水印：不支持此格式</p>
                      <p className="text-xs text-gray-400 leading-relaxed">
                        {draft.type === 'PPT'
                          ? 'PPT 文件下载后为原始 PPT 格式，系统无法在文件内嵌入水印。如需水印保护，建议转为 PDF 后上传。'
                          : `${draft.type} 文件下载后为原始格式，系统无法在文件内嵌入水印。如需水印保护，建议转为 PDF 后上传。`}
                      </p>
                    </div>
                  </div>
                )}

                <p className="text-xs text-gray-400 leading-relaxed">
                  下载水印仅支持 PDF 格式。在线预览水印对所有格式均有效。
                </p>
              </div>
          </Section>

          {/* 当前文件信息（只读预览） */}
          <Section title="当前文件信息" icon="ri-file-info-line">
            <div className="space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">格式</span>
                <span className="text-xs font-semibold text-gray-700">{draft.type}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">文件大小</span>
                <span className="text-xs font-semibold text-gray-700">{draft.fileSize}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">上传者</span>
                <span className="text-xs font-semibold text-gray-700">{draft.uploadedByName || draft.uploadedBy}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">上传日期</span>
                <span className="text-xs font-semibold text-gray-700">{draft.uploadDate}</span>
              </div>
            </div>
          </Section>

          {/* 底部保存按钮（右侧固定） */}
          <div className="flex flex-col gap-2">
            <button
              onClick={handleSave}
              disabled={saved || saving}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold text-white cursor-pointer whitespace-nowrap transition-all disabled:opacity-70"
              style={{ background: '#0069BD' }}
            >
              {saved ? (
                <>
                  <i className="ri-check-line text-sm"></i>
                  已保存，正在跳转…
                </>
              ) : saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-sm"></i>
                  保存中...
                </>
              ) : (
                <>
                  <i className="ri-save-line text-sm"></i>
                  保存更改
                </>
              )}
            </button>
            <button
              onClick={handleCancel}
              className="w-full py-2.5 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors"
            >
              取消
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
